import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"

import type { AttributionDateRange, ChannelExpense, ChannelStat } from "./types"
import type { AttributionModelKey } from "@/lib/attribution/attribution-models"
import { getPreviousPeriodRange } from "./get-comparison-range"
import {
  aggregateToTotals,
  computeKpiDeltas,
  computeDelta,
  type KpiTotals,
  type KpiDeltas,
  type DeltaResult,
} from "@/lib/analytics/deltas"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChannelTotals {
  revenue: number
  orders: number
  expense: number
  roi: number
}

export interface PeriodData {
  totals: KpiTotals
  channels: Map<string, ChannelTotals>
}

export interface ChannelDeltas {
  revenue: DeltaResult
  orders: DeltaResult
  expense: DeltaResult
  roi: DeltaResult
}

export interface ComparisonDeltas {
  totals: KpiDeltas
  channels: Map<string, ChannelDeltas>
  /** Source-level deltas. Key: "channelKey:sourceName" or "channelKey:parentSource:childSource" */
  sources: Map<string, ChannelDeltas>
}

export interface DailyDataPoint {
  date: string
  revenue: number
  orders: number
}

export interface UseComparisonDataOptions {
  integrationId: string
  dateRange: AttributionDateRange
  comparisonDateRange?: AttributionDateRange | null
  configRevision?: number
  attributionModel: AttributionModelKey
  minimumOrderTotal: number
  productIds?: number[]
  enabled?: boolean
  /** When false, skip previous-period fetches entirely. Defaults to true. */
  comparisonEnabled?: boolean
}

export interface UseComparisonDataResult {
  current: PeriodData | null
  previous: PeriodData | null
  deltas: ComparisonDeltas | null
  dailySeries: DailyDataPoint[]
  isLoading: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merge expenses into channel stats to get total expense per channel.
 * Returns a Map of channelSlug/channel → ChannelTotals.
 */
/** @internal exported for testing */
export function mergeExpensesIntoChannels(
  stats: ChannelStat[],
  expenses: ChannelExpense[],
): Map<string, ChannelTotals> {
  // Build expense lookup: channel_slug → total expense (sum of channel-level + source-level)
  const expenseByChannel = new Map<string, number>()
  for (const exp of expenses) {
    const key = exp.channel_slug
    expenseByChannel.set(key, (expenseByChannel.get(key) ?? 0) + exp.expense_amount)
  }

  const channels = new Map<string, ChannelTotals>()

  for (const stat of stats) {
    const channelKey = stat.channelSlug
    const totalExpense = expenseByChannel.get(stat.channelSlug) ?? 0
    const roi = totalExpense > 0 ? ((stat.revenue - totalExpense) / totalExpense) * 100 : 0

    channels.set(channelKey, {
      revenue: stat.revenue,
      orders: stat.orders,
      expense: totalExpense,
      roi,
    })
  }

  return channels
}

/**
 * Build PeriodData from raw stats + expenses.
 */
/** @internal exported for testing */
export function buildPeriodData(stats: ChannelStat[], expenses: ChannelExpense[]): PeriodData {
  const channels = mergeExpensesIntoChannels(stats, expenses)

  // Aggregate totals from channel data (not raw stats, since channels have expenses merged)
  const channelArray = Array.from(channels.values())
  const totals = aggregateToTotals(channelArray)

  return { totals, channels }
}

/**
 * Compute deltas for all channels using union of both periods' channel slugs.
 * Missing channels default to zeros; percent = null (displayed as "new").
 */
/** @internal exported for testing */
export function computeChannelDeltas(
  current: Map<string, ChannelTotals>,
  previous: Map<string, ChannelTotals>,
): Map<string, ChannelDeltas> {
  const zero: ChannelTotals = { revenue: 0, orders: 0, expense: 0, roi: 0 }
  const allKeys = new Set([...current.keys(), ...previous.keys()])
  const result = new Map<string, ChannelDeltas>()

  for (const key of allKeys) {
    const cur = current.get(key) ?? zero
    const prev = previous.get(key) ?? zero

    result.set(key, {
      revenue: computeDelta(cur.revenue, prev.revenue, "positive"),
      orders: computeDelta(cur.orders, prev.orders, "positive"),
      expense: computeDelta(cur.expense, prev.expense, "negative"),
      roi: computeDelta(cur.roi, prev.roi, "positive"),
    })
  }

  return result
}

/**
 * Compute revenue deltas for individual sources (and children) across two periods.
 * Key format: "channelKey:sourceName" for top-level sources,
 *             "channelKey:parentSource:childSource" for nested codes.
 */
/** @internal exported for testing */
export function computeSourceDeltas(
  currentStats: ChannelStat[],
  previousStats: ChannelStat[],
): Map<string, ChannelDeltas> {
  const flatZero: DeltaResult = { unit: "percent", absolute: 0, percent: null, direction: "flat", color: "gray" }

  const buildSourceMap = (stats: ChannelStat[]) => {
    const map = new Map<string, { revenue: number; orders: number }>()
    for (const stat of stats) {
      const channelKey = stat.channelSlug
      for (const source of stat.sources) {
        map.set(`${channelKey}:${source.sourceName}`, {
          revenue: source.revenue,
          orders: source.orders,
        })
        if (source.children) {
          for (const child of source.children) {
            map.set(`${channelKey}:${source.sourceName}:${child.sourceName}`, {
              revenue: child.revenue,
              orders: child.orders,
            })
          }
        }
      }
    }
    return map
  }

  const currentMap = buildSourceMap(currentStats)
  const previousMap = buildSourceMap(previousStats)

  const allKeys = new Set([...currentMap.keys(), ...previousMap.keys()])
  const result = new Map<string, ChannelDeltas>()

  for (const key of allKeys) {
    const cur = currentMap.get(key) ?? { revenue: 0, orders: 0 }
    const prev = previousMap.get(key) ?? { revenue: 0, orders: 0 }

    result.set(key, {
      revenue: computeDelta(cur.revenue, prev.revenue, "positive"),
      orders: computeDelta(cur.orders, prev.orders, "positive"),
      expense: flatZero,
      roi: flatZero,
    })
  }

  return result
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useComparisonData({
  integrationId,
  dateRange,
  comparisonDateRange,
  configRevision = 0,
  attributionModel,
  minimumOrderTotal,
  productIds = [],
  enabled = true,
  comparisonEnabled = true,
}: UseComparisonDataOptions): UseComparisonDataResult {
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const [current, setCurrent] = useState<PeriodData | null>(null)
  const [previous, setPrevious] = useState<PeriodData | null>(null)
  const [currentStats, setCurrentStats] = useState<ChannelStat[]>([])
  const [previousStats, setPreviousStats] = useState<ChannelStat[]>([])
  const [dailySeries, setDailySeries] = useState<DailyDataPoint[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Auto-compute previous period when no explicit comparison range provided
  const prevRange = useMemo(
    () => {
      if (!comparisonEnabled) return null
      return comparisonDateRange ?? getPreviousPeriodRange(dateRange)
    },
    [comparisonEnabled, comparisonDateRange, dateRange],
  )

  // Stable product_ids ref for dependency comparison
  const productIdsKey = productIds.join(",")

  const fetchRef = useRef(0)

  const refresh = useCallback(async () => {
    if (!integrationId || !isAuthenticated || !enabled) {
      setCurrent(null)
      setPrevious(null)
      setCurrentStats([])
      setPreviousStats([])
      setDailySeries([])
      setIsLoading(false)
      return
    }

    const fetchId = ++fetchRef.current
    setIsLoading(true)
    setCurrent(null)
    setPrevious(null)
    setCurrentStats([])
    setPreviousStats([])
    setDailySeries([])

    try {
      const buildParams = (range: AttributionDateRange): URLSearchParams => {
        const params = new URLSearchParams({
          integration_id: integrationId,
          month_start_utc: range.month_start_utc.toISOString(),
          month_end_utc: range.month_end_utc.toISOString(),
          timezone: range.timezone,
          attribution_model: attributionModel,
          minimum_order_total: Number.isFinite(minimumOrderTotal) ? String(minimumOrderTotal) : "0",
          config_revision: String(configRevision),
        })
        if (productIdsKey) {
          params.set("product_ids", productIdsKey)
        }
        return params
      }

      const currentParams = buildParams(dateRange)
      const dailyParams = buildParams(dateRange)

      // Build fetches: always fetch current + daily; conditionally fetch previous
      const fetches: Promise<Response>[] = [
        fetchWithAuth(`/api/integrations/shopify/attribution-stats?${currentParams}`),
        fetchWithAuth(`/api/integrations/shopify/channel-expenses?${currentParams}`),
        fetchWithAuth(`/api/integrations/shopify/attribution-stats/daily?${dailyParams}`),
      ]

      if (prevRange) {
        const prevParams = buildParams(prevRange)
        fetches.push(
          fetchWithAuth(`/api/integrations/shopify/attribution-stats?${prevParams}`),
          fetchWithAuth(`/api/integrations/shopify/channel-expenses?${prevParams}`),
        )
      }

      const results = await Promise.all(fetches)

      // Stale response guard
      if (fetchId !== fetchRef.current) return

      const [curStatsRes, curExpensesRes, dailyRes] = results
      const curStats: ChannelStat[] = curStatsRes.ok
        ? (await curStatsRes.json()).stats ?? []
        : []
      const curExpenses: ChannelExpense[] = curExpensesRes.ok
        ? (await curExpensesRes.json()).expenses ?? []
        : []

      const curData = buildPeriodData(curStats, curExpenses)
      setCurrent(curData)
      setCurrentStats(curStats)

      if (prevRange) {
        const prevStatsRes = results[3]
        const prevExpensesRes = results[4]
        const prevStats: ChannelStat[] = prevStatsRes.ok
          ? (await prevStatsRes.json()).stats ?? []
          : []
        const prevExpenses: ChannelExpense[] = prevExpensesRes.ok
          ? (await prevExpensesRes.json()).expenses ?? []
          : []
        setPrevious(buildPeriodData(prevStats, prevExpenses))
        setPreviousStats(prevStats)
      } else {
        setPrevious(null)
        setPreviousStats([])
      }

      // Daily series for sparklines
      if (dailyRes.ok) {
        const dailyData = await dailyRes.json()
        setDailySeries(dailyData.series ?? [])
      } else {
        setDailySeries([])
      }
    } catch (error) {
      console.error("[Comparison Data] Error fetching comparison data:", error)
      if (fetchId === fetchRef.current) {
        setCurrent(null)
        setPrevious(null)
        setCurrentStats([])
        setPreviousStats([])
        setDailySeries([])
      }
    } finally {
      if (fetchId === fetchRef.current) {
        setIsLoading(false)
      }
    }
  }, [
    integrationId,
    isAuthenticated,
    enabled,
    dateRange,
    prevRange,
    configRevision,
    attributionModel,
    minimumOrderTotal,
    productIdsKey,
    fetchWithAuth,
  ])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Compute deltas from current + previous period data
  const deltas = useMemo<ComparisonDeltas | null>(() => {
    if (!current || !previous) return null

    return {
      totals: computeKpiDeltas(current.totals, previous.totals),
      channels: computeChannelDeltas(current.channels, previous.channels),
      sources: computeSourceDeltas(currentStats, previousStats),
    }
  }, [current, previous, currentStats, previousStats])

  return { current, previous, deltas, dailySeries, isLoading }
}
